import { WizardQuestions_QuestionFragment } from "../../documents/WizardQuestions.generated.js";

/**
 * All the shell needs of a question to place it. The renderers narrow the
 * codegen'd union themselves; these helpers only need identity, ownership and
 * the headline fields.
 */
export type ShellQuestion = {
  questionId: string;
  itemId: string;
  itemTitle: string;
  /** Sorts in document order, so a question is placeable without its item. */
  itemPathSortable: string;
  type: string;
};

export type ItemQuestionPosition = { index: number; total: number };

/**
 * The question the view shows for the selected item.
 *
 * The URL carries the item, optionally with the question inside it. A named
 * question that is no longer in the list falls back to the item's first one:
 * an answer can drop the very question it was given on, and emptying the view
 * then would lose the item the user selected.
 */
export function getCurrentQuestion<Q extends ShellQuestion>(
  questions: ReadonlyArray<Q>,
  itemId: string | null,
  questionId?: string | null
): Q | null {
  if (!itemId) return null;

  const named = questionId
    ? questions.find(q => q.questionId === questionId)
    : undefined;

  if (named && named.itemId === itemId) return named;

  return questions.find(q => q.itemId === itemId) ?? null;
}

/** "n of m" counted within the question's own item. */
export function getItemQuestionPosition(
  questions: ReadonlyArray<ShellQuestion>,
  questionId: string
): ItemQuestionPosition | null {
  const question = questions.find(q => q.questionId === questionId);

  if (!question) return null;

  const itemQuestions = questions.filter(q => q.itemId === question.itemId);

  return {
    index: itemQuestions.findIndex(q => q.questionId === questionId) + 1,
    total: itemQuestions.length,
  };
}

/** A `null` label is a property group without a name. */
export type ItemQuestionStep<Q> = {
  question: Q;
  label: string | null;
  active: boolean;
};

/** The question's item's questions, in list order, as the header's steps. */
export function getItemQuestionSteps<
  Q extends ShellQuestion & {
    prompt?: string;
    productTypeTitle?: string;
    placeholderForTemplateTypeTitle?: string;
    props?: ReadonlyArray<{ visible: boolean; prop: { group: string } }>;
  },
>(
  questions: ReadonlyArray<Q>,
  questionId: string
): Array<ItemQuestionStep<Q>> | null {
  const question = questions.find(q => q.questionId === questionId);

  if (!question) return null;

  return questions
    .filter(q => q.itemId === question.itemId)
    .map(q => ({
      question: q,
      // A property question asks about exactly one group, so any visible prop
      // names it.
      label:
        q.type === "multiple-properties"
          ? q.props?.find(p => p.visible)?.prop.group || null
          : getQuestionPrompt(q),
      active: q.questionId === questionId,
    }));
}

export function resolveNextStep<Q extends ShellQuestion>(
  questions: ReadonlyArray<Q>,
  questionId: string | null,
  idle?: IdlePosition
): Q | null {
  return stepFrom(questions, questionId, idle, 1);
}

export function resolvePreviousStep<Q extends ShellQuestion>(
  questions: ReadonlyArray<Q>,
  questionId: string | null,
  idle?: IdlePosition
): Q | null {
  return stepFrom(questions, questionId, idle, -1);
}

/**
 * Where an item with no question of its own sits, so the idle state can still
 * step to its neighbours: the selected item's sortable path.
 */
export type IdlePosition = { pathSortable: string };

/**
 * Steps by position in the returned list — the view is browsed rather than
 * walked as a funnel, so the neighbouring question is the one the user means.
 * Resolving an answer's next question is a different rule and belongs to
 * `wizard.resolveStepAfterAnswer`, which keys on identity across a refetch.
 *
 * An item with no question is absent from the list, so there is no position to
 * step from; the neighbour is then the nearest question on either side of the
 * item in document order.
 */
function stepFrom<Q extends ShellQuestion>(
  questions: ReadonlyArray<Q>,
  questionId: string | null,
  idle: IdlePosition | undefined,
  direction: 1 | -1
): Q | null {
  const from = questionId
    ? questions.findIndex(q => q.questionId === questionId)
    : -1;

  if (from !== -1) return questions[from + direction] ?? null;

  if (!idle) return null;

  const neighbours = questions.filter(({ itemPathSortable }) =>
    direction === 1
      ? itemPathSortable > idle.pathSortable
      : itemPathSortable < idle.pathSortable
  );

  return direction === 1
    ? (neighbours[0] ?? null)
    : (neighbours[neighbours.length - 1] ?? null);
}

export type RememberedSelection = { itemId: string; pathSortable: string };

/**
 * Remembers the selected item so a selection lost to its deletion can step on
 * from where the item stood. The item can leave the document before the URL
 * drops it, so an unplaceable selection keeps what was remembered for it.
 */
export function trackSelection(
  remembered: RememberedSelection | null,
  selection: { itemId: string | null; pathSortable: string | undefined },
  itemExists: (itemId: string) => boolean
): {
  remembered: RememberedSelection | null;
  deleted: RememberedSelection | null;
} {
  const { itemId, pathSortable } = selection;

  if (itemId === null) {
    const deleted =
      remembered && !itemExists(remembered.itemId) ? remembered : null;
    return { remembered: null, deleted };
  }

  if (pathSortable !== undefined)
    return { remembered: { itemId, pathSortable }, deleted: null };

  return {
    remembered: remembered?.itemId === itemId ? remembered : null,
    deleted: null,
  };
}

/**
 * The question's headline: its own prompt, else the type it is asking about,
 * else the item it belongs to. A prompt is authored per item and is very often
 * empty.
 */
export function getQuestionPrompt(
  question: ShellQuestion & {
    prompt?: string;
    productTypeTitle?: string;
    placeholderForTemplateTypeTitle?: string;
  }
): string {
  return (
    question.prompt ||
    question.productTypeTitle ||
    question.placeholderForTemplateTypeTitle ||
    question.itemTitle
  );
}

/** The question list as the query returns it, or empty while it loads. */
export function getQuestions(
  result:
    | {
        __typename: string;
        questions?: Array<WizardQuestions_QuestionFragment>;
      }
    | null
    | undefined
): Array<WizardQuestions_QuestionFragment> {
  return result?.__typename === "QuoteWizardQuestionsResult"
    ? (result.questions ?? [])
    : [];
}

/**
 * Whether the forward button submits the question's staged answer. A
 * single-choice decision answers on the option click, so it has nothing staged.
 */
export function forwardSubmits(
  question: { __typename: string; behavior?: string } | null | undefined
): boolean {
  return (
    (question?.__typename === "WizardDecisionQuestion" &&
      question.behavior === "many") ||
    question?.__typename === "WizardMultiplePropertiesQuestion" ||
    question?.__typename === "WizardMultipleRecommendationsQuestion"
  );
}
